import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AdminModeProvider } from "./context/AdminModeContext";
import { Layout } from "./components/Layout";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Incidents from "./pages/Incidents";
import IncidentDetail from "./pages/IncidentDetail";
import Assessments from "./pages/Assessments";
import Organizations from "./pages/Organizations";
import OrganizationDetail from "./pages/OrganizationDetail";
// Claims temporarily hidden - not launching with claims functionality
// import Claims from "./pages/Claims";
// Staff & Board removed - focusing on user invites only
// import Staff from "./pages/Staff";
import UserManagement from "./pages/UserManagement";
import Alerts from "./pages/Alerts";
// Events temporarily hidden - not launching with events functionality
// import Events from "./pages/Events";
import Policies from "./pages/PoliciesV2"; // Updated to V2 with ownership clarity
import Training from "./pages/Training";
import WebMonitoring from "./pages/WebMonitoring";
import NetworkIntelligence from "./pages/NetworkIntelligence";
import Settings from "./pages/Settings";
import LMS from "./pages/LMS";
import AcceptInvite from "./pages/AcceptInvite";
import ExternalAssessment from "./pages/ExternalAssessment";
import CompleteOnboarding from "./pages/CompleteOnboarding";
import ALOAdmin from "./pages/ALOAdmin";
import ProfessionalSportsAssessment from "./pages/ProfessionalSportsAssessment";
import PublicIncidentReport from "./pages/PublicIncidentReport";
import BrokerDashboard from "./pages/BrokerDashboard";
import ProspectIntake from "./pages/ProspectIntake";
import CustomerOnboarding from "./pages/CustomerOnboarding";
import LossRuns from "./pages/LossRuns";
import PartnerMarketplace from "./pages/PartnerMarketplace";
import Guidance from "./pages/Guidance";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect to appropriate page if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    // Redirect brokers to their dashboard
    if (user?.role === 'broker') {
      return <Navigate to="/broker" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Smart redirect component that redirects based on user role
const SmartRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }
  
  // Redirect brokers to their dashboard
  if (user?.role === 'broker') {
    return <Navigate to="/broker" replace />;
  }
  
  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/assess" element={<ExternalAssessment />} />
      <Route path="/complete-onboarding" element={<CompleteOnboarding />} />
      <Route path="/customer-onboarding" element={<CustomerOnboarding />} />
      <Route path="/intake/:token" element={<ProspectIntake />} />
      <Route path="/report/:token" element={<PublicIncidentReport />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/incidents" element={<ProtectedRoute><Incidents /></ProtectedRoute>} />
      <Route path="/incidents/:id" element={<ProtectedRoute><IncidentDetail /></ProtectedRoute>} />
      <Route path="/assessments" element={<ProtectedRoute><Assessments /></ProtectedRoute>} />
      <Route path="/assessments/new" element={<ProtectedRoute><Assessments /></ProtectedRoute>} />
      <Route path="/assessments/:id" element={<ProtectedRoute><Assessments /></ProtectedRoute>} />
      <Route path="/assessments/professional-sports/:orgId" element={<ProtectedRoute><ProfessionalSportsAssessment /></ProtectedRoute>} />
      <Route path="/organizations" element={<ProtectedRoute><Organizations /></ProtectedRoute>} />
      <Route path="/organizations/:id" element={<ProtectedRoute><OrganizationDetail /></ProtectedRoute>} />
      {/* Claims routes temporarily hidden - not launching with claims functionality */}
      {/* <Route path="/claims" element={<ProtectedRoute><Claims /></ProtectedRoute>} /> */}
      {/* <Route path="/claims/upload" element={<ProtectedRoute><Claims /></ProtectedRoute>} /> */}
      {/* Staff & Board routes removed - focusing on user invites only */}
      {/* <Route path="/staff" element={<ProtectedRoute><Staff /></ProtectedRoute>} /> */}
      {/* <Route path="/staff/new" element={<ProtectedRoute><Staff /></ProtectedRoute>} /> */}
      <Route path="/users" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      {/* Events routes temporarily hidden - not launching with events functionality */}
      {/* <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} /> */}
      {/* <Route path="/events/new" element={<ProtectedRoute><Events /></ProtectedRoute>} /> */}
      <Route path="/policies" element={<ProtectedRoute><Policies /></ProtectedRoute>} />
      <Route path="/loss-runs" element={<ProtectedRoute><LossRuns /></ProtectedRoute>} />
      <Route path="/partners" element={<ProtectedRoute><PartnerMarketplace /></ProtectedRoute>} />
      <Route path="/guidance" element={<ProtectedRoute><Guidance /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
      <Route path="/lms" element={<ProtectedRoute><LMS /></ProtectedRoute>} />
      <Route path="/org-iq" element={<ProtectedRoute><WebMonitoring /></ProtectedRoute>} />
      <Route path="/monitoring" element={<ProtectedRoute><WebMonitoring /></ProtectedRoute>} />
      <Route path="/network-intelligence" element={<ProtectedRoute><NetworkIntelligence /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/alo-admin" element={<ProtectedRoute><ALOAdmin /></ProtectedRoute>} />
      <Route path="/broker" element={<ProtectedRoute><BrokerDashboard /></ProtectedRoute>} />
      
      {/* Redirect root based on user role */}
      <Route path="/" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />
      
      {/* Catch all - redirect based on user role */}
      <Route path="*" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AdminModeProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </AdminModeProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
